import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import {
  getLegacyShiftById,
  LegacyShiftQueryKey,
} from 'api/shifts/getLegacyShiftById'
import { WorkShift } from 'api/typings'

/**
 * Merges together all shift-related data for this shift ID even if different queries are used
 * @example useShiftDetails(shiftId, 'listingTiers', { enabled: false })
 */
export function useLegacyShiftDetails(
  shiftId: WorkShift['id'] | string,
  queryKey: LegacyShiftQueryKey,
  options?: Partial<UseQueryOptions<WorkShift>>
) {
  const queryClient = useQueryClient()

  return useQuery<WorkShift>({
    queryKey: ['shift', shiftId, queryKey].filter(Boolean),
    queryFn: async () => getLegacyShiftById(shiftId, queryKey),
    enabled: !!shiftId,
    retry: false,
    /** Do not remove existing data when additional data is fetched */
    placeholderData: keepPreviousData,
    /** Merge together all data related to this shift id */
    initialData: queryClient.getQueryData(['shift', shiftId]),
    ...options,
    staleTime: options?.staleTime ?? 10000,
    initialDataUpdatedAt: queryClient.getQueryState(['shift', shiftId])
      ?.dataUpdatedAt,
  })
}
